// SdsLookupSection.js
// Shared 3E catalog search UI, driven entirely by injected props so it renders the same way on the
// Safety page tab and inside the DEL Misc > SDS sub-tab (CalibrationLogSection idiom).

import React, { useEffect, useRef, useState } from "react";
import { SdsSearchForm } from "./SdsSearchForm";
import { SdsResultCard } from "./SdsResultCard";
import { SdsRequestForm } from "./SdsRequestForm";
import { SdsHelpLine } from "./SdsHelpLine";
import { CAS_RE, LIBRARY_MIN_TEXT_CHARS, MIN_TEXT_CHARS, useSdsSearch } from "./useSdsSearch";
import { normalizeProductUid } from "../../../models/Del/DelSdsReference";
import listStyles from "../ui/listTab.module.css";
import tableStyles from "../ui/listTable.module.css";
import styles from "./SdsLookupSection.module.css";

const TAB_OFFLINE_MESSAGE = "SDS search needs a connection.";
const DEL_OFFLINE_MESSAGE =
  "SDS search needs a connection. Attached SDS are listed above; opening them needs a connection.";

const formatWait = (seconds) =>
  seconds === 1 ? "Try again in 1 second." : `Try again in ${seconds} seconds.`;

// A CAS number is not a product name, so it never prefills the Request SDS form
const materialNameFrom = (query) => (CAS_RE.test(query) ? "" : query);

/**
 * @param {boolean} isOnline - useSdsOnline().isServerReachable (tab) or connectionStatus === "connected" (DEL)
 * @param {object|null} sdsStatus - { configured, request_enabled } from /api/sds/status, null while loading
 * @param {function} [onAttach] - (hit) => { ok, duplicate }; DEL edit context only, shows Attach on cards
 * @param {string[]} [attachedProductUids] - product_uid values already attached to the DEL
 * @param {string} [initialQuery] - prefill and run (Requests -> Search handoff, ?q=)
 * @param {string} [delId] - forwarded to the Request SDS form (phase 5)
 * @param {function} showNotification - notificationActions.showNotification
 */
export const SdsLookupSection = ({
  isOnline,
  sdsStatus,
  onAttach,
  attachedProductUids = [],
  initialQuery = "",
  delId,
  showNotification,
}) => {
  // "library" = the S3 SDS library (PDFs exported from 3E Protect) instead of the 3E API
  const isLibrary = sdsStatus?.source === "library";
  const {
    criteria,
    setCriteria,
    results,
    page,
    moreRecords,
    totalRecords,
    status,
    errorMessage,
    retryAfter,
    lastSearch,
    search,
    nextPage,
    prevPage,
    reset,
  } = useSdsSearch({ minTextChars: isLibrary ? LIBRARY_MIN_TEXT_CHARS : MIN_TEXT_CHARS });

  const isDelContext = typeof onAttach === "function";
  const requestEnabled = !!sdsStatus?.request_enabled;
  const notConfigured = !!sdsStatus && sdsStatus.configured === false;
  const searchDisabled = !isOnline || status === "loading";

  // The Request SDS form is forced open by an empty result and otherwise toggled by the link.
  // The forced form is keyed and prefilled from the search that came back empty (lastSearch), never
  // from the live box, so editing the query does not remount it and wipe what the user typed.
  const [requestFormOpen, setRequestFormOpen] = useState(false);
  const requestFormForced = requestEnabled && status === "empty" && !!lastSearch;
  const showRequestForm = requestEnabled && (requestFormForced || requestFormOpen);
  const emptyQuery = lastSearch?.query || "";
  const liveQuery = (criteria.query || "").trim();
  const requestFormKey = requestFormForced
    ? `empty-${emptyQuery}|${lastSearch.manufacturerName}|${lastSearch.partNumber}`
    : "manual";

  // Seed the box from initialQuery and run once per distinct value; the query then belongs to the user
  const seededQueryRef = useRef(null);
  const pendingSearchRef = useRef(false);
  useEffect(() => {
    const value = (initialQuery || "").trim();
    if (!value || seededQueryRef.current === value) return;
    seededQueryRef.current = value;
    setCriteria((current) => ({ ...current, query: value }));
    pendingSearchRef.current = true;
  }, [initialQuery, setCriteria]);

  useEffect(() => {
    if (!pendingSearchRef.current || !isOnline || notConfigured) return;
    if ((criteria.query || "").trim() !== seededQueryRef.current) return;
    pendingSearchRef.current = false;
    search(1);
  }, [criteria.query, isOnline, notConfigured, search]);

  const handleChange = (patch) => setCriteria((current) => ({ ...current, ...patch }));
  const handleSubmit = () => {
    if (searchDisabled) return;
    search(1);
  };
  const handleRetry = () => search(page || 1);

  if (notConfigured) {
    return (
      <div className={styles.section} data-testid="sds-lookup-section">
        <div className={tableStyles.emptyContainer}>
          SDS lookup is not set up on this server yet. Contact the safety team.
        </div>
        <SdsHelpLine sdsStatus={sdsStatus} />
      </div>
    );
  }

  const renderResults = () => {
    if (status === "loading") {
      return (
        <div className={tableStyles.loadingContainer} data-testid="sds-loading">
          <div className={tableStyles.loadingSpinner} />
          <span>{isLibrary ? "Searching the SDS library..." : "Searching the 3E catalog..."}</span>
        </div>
      );
    }
    if (status === "error") {
      return (
        <div className={tableStyles.errorContainer} role="alert">
          <div className={tableStyles.errorMessage}>{errorMessage}</div>
          {retryAfter && <div className={styles.waitText}>{formatWait(retryAfter)}</div>}
          <button
            type="button"
            className={tableStyles.retryButton}
            onClick={handleRetry}
            disabled={!isOnline}
          >
            Retry
          </button>
        </div>
      );
    }
    if (status === "empty") {
      return (
        <div className={styles.emptyState}>
          <div className={tableStyles.emptyContainer}>
            {isLibrary ? "No SDS in EWI's library matches that search." : "Not in EWI's 3E catalog yet."}
          </div>
          {requestEnabled && (
            <div className={styles.emptyHint}>
              Request it below and 3E will add it to EWI&apos;s catalog.
            </div>
          )}
        </div>
      );
    }
    if (status === "success") {
      const showPagination = moreRecords || page > 1;
      return (
        <>
          <div className={styles.countLine}>
            {totalRecords
              ? `Showing ${results.length} of ${totalRecords} matches`
              : `Showing ${results.length} matches`}
            {page > 1 ? ` (page ${page})` : ""}
          </div>
          <div className={styles.resultList}>
            {results.map((hit) => {
              const hitUid = normalizeProductUid(hit.product_uid);
              return (
                <SdsResultCard
                  key={hit.product_uid || `${hit.product_name}-${hit.manufacturer_name}`}
                  hit={hit}
                  onAttach={onAttach}
                  isAttached={!!hitUid && attachedProductUids.includes(hitUid)}
                  showNotification={showNotification}
                />
              );
            })}
          </div>
          {showPagination && (
            <div className={listStyles.paginationControls}>
              <div className={listStyles.paginationLeftControls}>
                <button type="button" onClick={prevPage} disabled={page <= 1 || !isOnline}>
                  Previous
                </button>
                <button type="button" onClick={nextPage} disabled={!moreRecords || !isOnline}>
                  Next
                </button>
              </div>
              <span>Page {page}</span>
            </div>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <div className={styles.section} data-testid="sds-lookup-section" data-del-id={delId || undefined}>
      {!isOnline && (
        <div className={styles.offlineBanner} role="status">
          {isDelContext ? DEL_OFFLINE_MESSAGE : TAB_OFFLINE_MESSAGE}
        </div>
      )}

      <SdsSearchForm
        criteria={criteria}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onClear={reset}
        disabled={searchDisabled}
        errorMessage={status === "error" ? null : errorMessage}
      />

      <SdsHelpLine sdsStatus={sdsStatus} />

      {requestEnabled && !requestFormForced && (
        <div className={styles.requestLinkRow}>
          <button
            type="button"
            className={styles.linkButton}
            aria-expanded={requestFormOpen}
            onClick={() => setRequestFormOpen((open) => !open)}
          >
            {requestFormOpen ? "Hide request form" : "Can't find it? Request SDS"}
          </button>
        </div>
      )}

      {renderResults()}

      {showRequestForm && (
        <SdsRequestForm
          key={requestFormKey}
          initialMaterialName={materialNameFrom(requestFormForced ? emptyQuery : liveQuery)}
          initialManufacturerName={
            requestFormForced ? lastSearch.manufacturerName : criteria.manufacturerName
          }
          initialProductNumber={requestFormForced ? lastSearch.partNumber : criteria.partNumber}
          delId={delId}
          isOnline={isOnline}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};
